import React from "react";
import PropTypes from "prop-types";
import pluralize from "pluralize";
import styled from "styled-components";

import Date from "../../../ui/BWGrid/columns/Date";
import OptionalColumn from "../../../ui/BWGrid/columns/OptionalColumn";
import { BWGridAPI, GridHeader } from "../../../ui/BWGrid";
import PurchaseOrderChangeRecords from "../../../../models/PurchaseOrderChangeRecords";
import { GridTable } from "../../../ui/BWGrid";
import { Paper } from "../../../mui/core";
import { PRIMARY_DATE_FORMAT } from "../../../../constants/formats";
import TagText from "../../../ui/Tags/TagText";
import { BWIntent } from "../../../ui/BWIntent";

// Regex to parse status snapshot format: "v{version};{status}[ -> {revisionStatus}]"
const STATUS_SNAPSHOT_REGEX = /^v(\d+);(.+?)(?:\s*->\s*(.+))?$/;

// Intent mapping for PO statuses (mirrors POStatusTag)
const STATUS_INTENT = {
  Issued: BWIntent.SUCCESS,
  "Issued, Revised": BWIntent.SUCCESS,
  Approved: BWIntent.PRIMARY,
  "Pending Approval": BWIntent.PRIMARY,
};

const ChipsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

/**
 * Parses a status snapshot string of the format "v{version};{status}[ -> {revisionStatus}]".
 * Returns null for invalid or empty input.
 */
export const parseStatusSnapshot = snapshot => {
  if (!snapshot) return null;
  const match = snapshot.match(STATUS_SNAPSHOT_REGEX);
  if (!match) return null;
  return {
    version: parseInt(match[1], 10),
    status: match[2].trim(),
    revisionStatus: match[3]?.trim() || null,
  };
};

const renderStatusChips = snapshot => {
  const parsed = parseStatusSnapshot(snapshot);
  if (!parsed) return null;
  const { version, status, revisionStatus } = parsed;
  return (
    <ChipsWrapper>
      {version > 0 && <TagText intent={BWIntent.NONE}>v{version}</TagText>}
      <TagText intent={STATUS_INTENT[status] || BWIntent.NONE}>
        {status}
      </TagText>
      {revisionStatus && (
        <TagText intent={BWIntent.WARNING} textIntent="dark">
          CO {revisionStatus}
        </TagText>
      )}
    </ChipsWrapper>
  );
};

// Column definitions
const columns = [
  "createdAt",
  "previousStatus",
  "newStatus",
  "user.person.name",
];

// Column configuration with rendering and filtering options
export const getColumnOptions = () => ({
  createdAt: {
    title: "Created At",
    bold: true,
    render: ({ id, createdAt }) =>
      id && <Date date={createdAt} format={PRIMARY_DATE_FORMAT} includeTime />,
    filter: "picker",
  },
  previousStatus: {
    title: "Previous Status",
    filter: true,
    render: ({ id, previousStatus }) =>
      id && (
        <OptionalColumn>{renderStatusChips(previousStatus)}</OptionalColumn>
      ),
  },
  newStatus: {
    title: "New Status",
    filter: true,
    render: ({ id, newStatus }) =>
      id && <OptionalColumn>{renderStatusChips(newStatus)}</OptionalColumn>,
  },
  "user.person.name": {
    title: "Modified By",
    filter: true,
    render: ({ id, user }) =>
      id && <OptionalColumn>{user?.person?.name}</OptionalColumn>,
  },
});

const POChangeRecordsList = ({
  dataComponent,
  poId,
  onRowClick,
  changeRecordsCount,
}) => {
  return (
    <Paper>
      <BWGridAPI
        dataComponent={dataComponent}
        model={PurchaseOrderChangeRecords}
        apiRoute="purchase-order-change-records"
        includes={["user.person"]}
        defaultSorting={[{ columnName: "createdAt", direction: "desc" }]}
        rootFilters={{
          $where: {
            purchaseOrderId: poId,
          },
        }}
      >
        <GridHeader
          headerText={pluralize(
            "PO Change Record",
            dataComponent.totalRows || changeRecordsCount,
            true
          )}
        />
        <GridTable
          columns={columns}
          columnOptions={getColumnOptions()}
          onClick={onRowClick}
        />
      </BWGridAPI>
    </Paper>
  );
};

POChangeRecordsList.propTypes = {
  poId: PropTypes.string.isRequired,
  dataComponent: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
  changeRecordsCount: PropTypes.number,
};

export default POChangeRecordsList;
